import { useMemo, useState } from "react";
import { diffWordsWithSpace } from "diff";

// ElevenLabs Creator-plan rate is ~$0.30 per 1,000 chars → $0.0003/char.
// We surface this just to give the user a rough cost-impact eyeball.
const ELEVENLABS_USD_PER_CHAR = 0.0003;

/**
 * Word-level inline diff of the OLD narration vs the NEW narration with
 * red/green highlights, plus a header showing char delta + estimated
 * ElevenLabs cost delta. Collapsed by default so it doesn't dominate the
 * editor — user clicks to expand.
 */
export default function NarrationDiff({ oldText, newText }) {
  const [open, setOpen] = useState(false);

  const { parts, oldChars, newChars, additions, deletions } = useMemo(() => {
    const o = oldText || "";
    const n = newText || "";
    if (o === n) {
      return { parts: [], oldChars: o.length, newChars: n.length, additions: 0, deletions: 0 };
    }
    const segs = diffWordsWithSpace(o, n);
    let added = 0;
    let removed = 0;
    for (const s of segs) {
      if (s.added) added += s.value.length;
      else if (s.removed) removed += s.value.length;
    }
    return {
      parts: segs,
      oldChars: o.length,
      newChars: n.length,
      additions: added,
      deletions: removed,
    };
  }, [oldText, newText]);

  if (parts.length === 0) return null;

  const charDelta = newChars - oldChars;
  // Cost delta is calculated on the *new* character count because
  // ElevenLabs bills the full re-synthesis, not just changed bytes.
  const costEstimate = newChars * ELEVENLABS_USD_PER_CHAR;
  const oldCostEstimate = oldChars * ELEVENLABS_USD_PER_CHAR;
  const costDelta = costEstimate - oldCostEstimate;

  return (
    <div
      className="mt-2 border border-yellow-400/30 bg-yellow-400/[0.03] rounded-sm"
      data-testid="narration-diff"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="narration-diff-toggle"
        className="w-full px-3 py-2 flex items-center justify-between gap-3 text-left hover:bg-yellow-400/5 transition-colors"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300">
          {open ? "▾" : "▸"} Diff vs. saved · +{additions} / -{deletions} chars
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex gap-3">
          <span>
            {oldChars.toLocaleString()} → {newChars.toLocaleString()} chars
          </span>
          <span className={charDelta >= 0 ? "text-yellow-300" : "text-emerald-300"}>
            {charDelta >= 0 ? "+" : ""}{charDelta}
          </span>
          <span title={`Re-TTS costs ≈ $${ELEVENLABS_USD_PER_CHAR.toFixed(4)}/char on ElevenLabs Creator plan`}>
            ≈ ${costEstimate.toFixed(3)}
            {costDelta !== 0 && (
              <span className={costDelta >= 0 ? " text-yellow-300" : " text-emerald-300"}>
                {" "}({costDelta >= 0 ? "+" : ""}${costDelta.toFixed(3)})
              </span>
            )}
          </span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-yellow-400/15">
          <pre
            data-testid="narration-diff-body"
            className="text-xs leading-relaxed font-mono whitespace-pre-wrap break-words text-zinc-200 max-h-[280px] overflow-y-auto"
          >
            {parts.map((p, i) => {
              if (p.added) {
                return (
                  <span
                    key={i}
                    className="bg-emerald-400/20 text-emerald-200 px-0.5 rounded-sm"
                    title="added"
                  >
                    {p.value}
                  </span>
                );
              }
              if (p.removed) {
                return (
                  <span
                    key={i}
                    className="bg-red-500/20 text-red-200 line-through px-0.5 rounded-sm"
                    title="removed"
                  >
                    {p.value}
                  </span>
                );
              }
              return (
                <span key={i} className="text-zinc-400">
                  {p.value}
                </span>
              );
            })}
          </pre>
        </div>
      )}
    </div>
  );
}
